import React, { useEffect, useRef, useState } from 'react';
import { PrismicRichText } from '@prismicio/react';
import styled from '@emotion/styled';

import { CmsText } from '../../cms/types';
import { KNOW_MORE, SEE_LESS } from '../../home/locale';
import { hasValidCmsText } from '../../home/utils';
import { isButtonToDisplay } from '../utils';

type Alignment = 'left' | 'center' | 'right';

const StyledContainer = styled.div<{ alignment: Alignment; isTextExtended: boolean }>`
  width: 100%;
  margin: auto;
  max-width: 1120px;
  padding: 0 16px;
  margin-bottom: 16px;

  .seo-description-content {
    text-align: ${(props) => props.alignment};
    ${(props) =>
      props.isTextExtended
        ? ''
        : `
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 3;
      overflow: hidden;
    `}
  }

  .seo-description-button {
    display: flex;
    margin-left: auto;
    margin-top: 8px;
    font-size: 1.4rem;
  }
`;

type Props = {
  seo_description: CmsText;
  alignment: Alignment;
};

export const SeoDescription = ({ seo_description, alignment }: Props) => {
  const textRef = useRef<HTMLDivElement | null>(null);
  const [isTextExtended, setIsTextExtended] = useState(false);
  const [displayButton, setDisplayButton] = useState(false);

  useEffect(() => {
    const checkButtonDisplay = () => {
      setDisplayButton(isButtonToDisplay(textRef));
    };

    checkButtonDisplay();

    return () => {
      setIsTextExtended(false);
    };
  }, [JSON.stringify(seo_description), textRef?.current]);

  useEffect(() => {
    setDisplayButton(isTextExtended || isButtonToDisplay(textRef));
  }, [isTextExtended]);

  if (!hasValidCmsText(seo_description)) {
    return null;
  }

  return (
    <StyledContainer
      id="seo-description-block"
      alignment={alignment}
      isTextExtended={isTextExtended}
    >
      <div
        className={`seo-description-content`}
        ref={textRef}
        aria-hidden={displayButton && !isTextExtended}
      >
        <PrismicRichText field={seo_description} />
      </div>
      {displayButton && (
        <button
          className="seo-description-button anchor-animated"
          aria-controls="seo-description-content"
          aria-expanded={isTextExtended}
          onClick={() => setIsTextExtended(!isTextExtended)}
          data-testid={`seo-description-show-${isTextExtended ? 'less' : 'more'}-button`}
          aria-label={`Bouton pour ${isTextExtended ? 'réduire' : 'afficher'} la description`}
        >
          {isTextExtended ? SEE_LESS : KNOW_MORE}
        </button>
      )}
    </StyledContainer>
  );
};
